/**
 * VOD Video Player with Google IMA Ads
 * 
 * A clean, simple implementation that works like YouTube:
 * 1. Click play → Pre-roll ad plays (if enabled)
 * 2. Ad ends → Content video plays
 * 3. Support for custom ads and Google ads
 * 4. HLS streaming support for Cloudflare Stream videos
 */

import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import Hls from 'hls.js';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Google's test VAST tag for development
const TEST_VAST_TAG = 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=640x480&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=';

const VODPlayer = forwardRef(({
  src,
  src4k,        // NEW: 4K video source
  srcHd,        // NEW: HD (1080p) video source
  videoSlug,    // For fetching video-specific ad cue points
  videoId,      // For ad impression tracking
  poster,
  enableAds = true,
  autoPlay = false,
  skipPreRollProp = false,  // Skip pre-roll ad (e.g. when resuming playback)
  isLandscapeFullscreen = false, // Mobile landscape fullscreen mode — auto-hide controls
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate,
  onAdStart,
  onAdEnd,
  onAdSkip,
  onError,
  onQualityChange,
  className = '',
  style = {},
}, ref) => {
  // Refs
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const adVideoRef = useRef(null); // Dedicated video element for ads (required for mobile)
  const adContainerRef = useRef(null);
  const adsLoaderRef = useRef(null);
  const adsManagerRef = useRef(null);
  const adDisplayContainerRef = useRef(null);
  const autoPlayTriggeredRef = useRef(false);
  const canvasRef = useRef(null);
  const hlsRef = useRef(null); // HLS.js instance for streaming
  
  // State
  const [playerState, setPlayerState] = useState('idle'); // idle, loading, playing, paused, ad-playing
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [vastTag, setVastTag] = useState(null); // Only set when a real VAST tag is configured — never default to test tag
  const [imaReady, setImaReady] = useState(false);
  const [adRequested, setAdRequested] = useState(false);
  const [pausedFrame, setPausedFrame] = useState(null); // Store paused frame as data URL
  const [isHls, setIsHls] = useState(false); // Track if current source is HLS
  
  // Mid-roll ad state
  const [midRollCuePoints, setMidRollCuePoints] = useState([]); // Array of mid-roll objects with timestamp, ad_type, custom_url
  const [playedMidRolls, setPlayedMidRolls] = useState(new Set()); // Track which cue points have played
  const midRollInProgressRef = useRef(false); // Prevent double-triggering mid-rolls
  const [currentCustomAd, setCurrentCustomAd] = useState(null); // Current custom ad playing
  const [customAdTime, setCustomAdTime] = useState(0); // Current playback time of custom ad
  const isCustomAdPlaying = useRef(false); // Track if a custom (non-IMA) ad is playing
  const isVideoElementFullscreen = useRef(false); // Track iOS video-element fullscreen (overlays invisible)
  const usingMainVideoForAd = useRef(false); // When true, ad is playing on main videoRef (iOS fullscreen fallback)
  const savedVideoState = useRef({ src: '', currentTime: 0 }); // Save main video state when swapping for ad
  
  // Mobile landscape fullscreen — tap-to-show/auto-hide controls (3s timeout)
  const [mobileControlsVisible, setMobileControlsVisible] = useState(false);
  const mobileControlsTimerRef = useRef(null);
  
  const showMobileControlsBriefly = useCallback(() => {
    setMobileControlsVisible(true);
    if (mobileControlsTimerRef.current) clearTimeout(mobileControlsTimerRef.current);
    mobileControlsTimerRef.current = setTimeout(() => {
      setMobileControlsVisible(false);
    }, 3000);
  }, []);
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (mobileControlsTimerRef.current) clearTimeout(mobileControlsTimerRef.current);
    };
  }, []);
  
  // Hide controls when exiting landscape fullscreen
  useEffect(() => {
    if (!isLandscapeFullscreen) {
      setMobileControlsVisible(false);
      if (mobileControlsTimerRef.current) clearTimeout(mobileControlsTimerRef.current);
    }
  }, [isLandscapeFullscreen]);
  
  // Determine if controls should be visible
  const shouldShowControls = !isLandscapeFullscreen || mobileControlsVisible;
  
  // Video-specific ad settings
  const [skipPreRoll, setSkipPreRoll] = useState(skipPreRollProp); // Skip pre-roll if video has custom settings with pre-roll disabled
  const [useVideoSpecificAds, setUseVideoSpecificAds] = useState(false); // Track if using video-specific settings
  const [adSettingsLoaded, setAdSettingsLoaded] = useState(false); // Track if ad settings have been loaded
  const [globalPreRollAd, setGlobalPreRollAd] = useState(null); // Store global pre-roll custom ad settings
  // Sync skipPreRoll prop (e.g. when user clicks "Resume" after page load)
  useEffect(() => { if (skipPreRollProp) setSkipPreRoll(true); }, [skipPreRollProp]);

  
  // NEW: Quality control state
  const [currentQuality, setCurrentQuality] = useState('auto'); // 'auto', '4k', 'hd', or HLS level index
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [availableQualities, setAvailableQualities] = useState([]);
  const [hlsLevels, setHlsLevels] = useState([]); // HLS quality levels from manifest
  
  // NEW: Cast state
  const [airplayAvailable, setAirplayAvailable] = useState(false);
  const [chromecastAvailable, setChromecastAvailable] = useState(false);
  
  // Determine available qualities from R2 URLs (non-HLS sources)
  useEffect(() => {
    // Only build R2-based qualities if we don't have HLS levels
    if (hlsLevels.length > 0) return;
    
    const qualities = [];
    if (src4k) qualities.push({ id: '4k', label: '4K', src: src4k, height: 2160 });
    if (srcHd) qualities.push({ id: 'hd', label: '1080p', src: srcHd, height: 1080 });
    if (src && src !== src4k && src !== srcHd) qualities.push({ id: 'sd', label: 'SD', src: src, height: 480 });
    setAvailableQualities(qualities);
    
    // Default to 4K if available, then HD, then SD
    if (src4k) {
      setCurrentQuality('4k');
    } else if (srcHd) {
      setCurrentQuality('hd');
    } else {
      setCurrentQuality('sd');
    }
  }, [src, src4k, srcHd, hlsLevels.length]);
  
  // Check AirPlay availability - Also check if on Safari/iOS
  useEffect(() => {
    // Check Safari/WebKit browser
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isSafari || isIOS) {
      setAirplayAvailable(true);
    }
    
    if (videoRef.current && 'webkitShowPlaybackTargetPicker' in videoRef.current) {
      setAirplayAvailable(true);
    }
    
    // Listen for AirPlay availability changes
    const video = videoRef.current;
    video?.addEventListener('webkitplaybacktargetavailabilitychanged', (e) => {
      setAirplayAvailable(e.availability === 'available');
    });
    return () => {
      video?.removeEventListener('webkitplaybacktargetavailabilitychanged', () => {});
    };
  }, []);
  
  // Check Chromecast availability - Always show on Chrome
  useEffect(() => {
    // Check if Chrome browser
    const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge|Edg/.test(navigator.userAgent);
    
    if (isChrome) {
      setChromecastAvailable(true);
    }
    
    // Also try remote playback API
    if (videoRef.current && 'remote' in videoRef.current) {
      videoRef.current.remote.watchAvailability((available) => {
        setChromecastAvailable(available || isChrome);
      }).catch(() => {
        // Remote playback not supported, still show on Chrome
        setChromecastAvailable(isChrome);
      });
    }
  }, []);
  
  // Get current video source based on quality — 4K first
  const getCurrentSrc = useCallback(() => {
    switch (currentQuality) {
      case '4k': return src4k || srcHd || src;
      case 'hd': return srcHd || src4k || src;
      case 'sd': return src || srcHd || src4k;
      default: return src4k || srcHd || src; // Default: use highest available
    }
  }, [currentQuality, src, src4k, srcHd]);
  
  // Initialize HLS.js for streaming sources (.m3u8)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const currentSrc = getCurrentSrc();
    const isHlsSource = currentSrc?.includes('.m3u8') || currentSrc?.includes('cloudflarestream.com');
    
    setIsHls(isHlsSource);
    
    if (isHlsSource) {
      // Destroy existing HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      
      if (Hls.isSupported()) {
        console.log('[VODPlayer] Initializing HLS.js for:', currentSrc);
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
        });
        
        hls.loadSource(currentSrc);
        hls.attachMedia(video);
        
        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          console.log('[VODPlayer] HLS manifest parsed, levels:', data.levels?.length);
          
          // Extract quality levels from HLS manifest
          if (data.levels && data.levels.length > 0) {
            const levels = data.levels.map((level, index) => ({
              index,
              height: level.height,
              width: level.width,
              bitrate: level.bitrate,
              label: level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)}kbps`,
            }));
            
            // Sort by height descending (highest quality first)
            levels.sort((a, b) => (b.height || b.bitrate) - (a.height || a.bitrate));
            setHlsLevels(levels);
            
            // Build quality menu from HLS levels (no Auto option)
            const qualities = levels.map(level => ({
              id: `hls-${level.index}`,
              label: level.height >= 2160 ? '4K' : level.height >= 1440 ? '1440p' : level.height >= 1080 ? '1080p' : level.height >= 720 ? '720p' : level.height >= 480 ? '480p' : level.height >= 360 ? '360p' : level.label,
              hlsIndex: level.index,
              height: level.height,
              bitrate: level.bitrate,
            }));
            setAvailableQualities(qualities);
            
            // Default to highest quality (4K if available)
            const highest = levels[0]; // Already sorted descending
            if (highest) {
              const highestId = `hls-${highest.index}`;
              setCurrentQuality(highestId);
              hls.currentLevel = highest.index; // Force highest level
              console.log('[VODPlayer] Defaulting to highest quality:', highest.height + 'p');
            }
            
            console.log('[VODPlayer] HLS quality levels:', qualities.map(q => q.label));
          }
        });
        
        hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
          const level = hls.levels[data.level];
          const label = level?.height >= 2160 ? '4K' : level?.height >= 1080 ? '1080p' : level?.height >= 720 ? '720p' : level?.height + 'p';
          console.log('[VODPlayer] HLS level switched to:', data.level, label);
          // Update the quality label to reflect actual rendering quality
          setCurrentQuality(`hls-${data.level}`);
        });
        
        // Auto-downgrade: if buffering too long, drop to lower quality
        hls.on(Hls.Events.FRAG_LOAD_EMERGENCY_ABORTED, () => {
          console.log('[VODPlayer] Fragment load emergency abort — auto-downgrading quality');
          // HLS.js handles this automatically when currentLevel is set to a specific level
          // by falling back. We let it recover and update the label.
        });
        
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('[VODPlayer] HLS error:', data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
                break;
            }
          }
        });
        
        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        console.log('[VODPlayer] Using native HLS support');
        video.src = currentSrc;
      }
    } else {
      // Regular video source
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    }
    
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [getCurrentSrc]);
  
  // Handle quality change
  const handleQualityChange = useCallback((qualityId) => {
    setCurrentQuality(qualityId);
    setShowQualityMenu(false);
    
    // HLS quality level switching
    if (hlsRef.current && qualityId.startsWith('hls-')) {
      const levelIndex = parseInt(qualityId.replace('hls-', ''));
      hlsRef.current.currentLevel = levelIndex;
      console.log('[VODPlayer] Switched HLS level to:', levelIndex);
      onQualityChange?.(qualityId);
      return;
    }
    
    // R2 URL quality switching (non-HLS)
    const currentTimePos = videoRef.current?.currentTime || 0;
    
    // Update video source and restore position — always auto-play after quality switch
    if (videoRef.current) {
      const newSrc = qualityId === '4k' ? (src4k || srcHd || src) 
                   : qualityId === 'hd' ? (srcHd || src) 
                   : src;
      
      if (videoRef.current.src !== newSrc && newSrc) {
        const video = videoRef.current;
        video.src = newSrc;
        
        // Wait for new source to be ready, then seek and play
        const onCanPlay = () => {
          video.removeEventListener('canplay', onCanPlay);
          video.currentTime = currentTimePos;
          video.play().catch(() => {});
          setPlayerState('playing');
        };
        video.addEventListener('canplay', onCanPlay);
        video.load();
      }
    }
    
    onQualityChange?.(qualityId);
  }, [playerState, src, src4k, srcHd, onQualityChange]);

  // Auto-downgrade for R2 sources: detect buffering and drop quality
  useEffect(() => {
    const video = videoRef.current;
    if (!video || hlsRef.current) return; // Skip for HLS (HLS handles its own ABR)
    
    let bufferingStart = null;
    let downgradeApplied = false;
    
    const onWaiting = () => {
      bufferingStart = Date.now();
    };
    
    const onPlaying = () => {
      if (bufferingStart && !downgradeApplied) {
        const bufferDuration = Date.now() - bufferingStart;
        // If buffering lasted more than 3 seconds, downgrade quality
        if (bufferDuration > 3000) {
          if (currentQuality === '4k' && srcHd) {
            console.log('[VODPlayer] Auto-downgrade: 4K → 1080p (buffering detected)');
            handleQualityChange('hd');
            downgradeApplied = true;
          } else if (currentQuality === 'hd' && src) {
            console.log('[VODPlayer] Auto-downgrade: 1080p → SD (buffering detected)');
            handleQualityChange('sd');
            downgradeApplied = true;
          }
        }
      }
      bufferingStart = null;
    };
    
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    return () => {
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
    };
  }, [currentQuality, src, srcHd, handleQualityChange]);
  
  // AirPlay handler
  const handleAirPlay = useCallback(() => {
    if (videoRef.current) {
      if ('webkitShowPlaybackTargetPicker' in videoRef.current) {
        videoRef.current.webkitShowPlaybackTargetPicker();
      } else {
        // Fallback: Try to use native video AirPlay
        alert('AirPlay is available on Safari and iOS devices. Look for the AirPlay icon in the native video controls.');
      }
    }
  }, []);
  
  // Chromecast handler
  const handleChromecast = useCallback(async () => {
    if (videoRef.current) {
      if ('remote' in videoRef.current) {
        try {
          await videoRef.current.remote.prompt();
        } catch (err) {
          console.log('[VODPlayer] Chromecast prompt error:', err);
          // Show instruction for users
          alert('To cast this video, click the three dots menu in Chrome and select "Cast"');
        }
      } else {
        // Fallback instruction
        alert('To cast this video, click the three dots menu in Chrome and select "Cast"');
      }
    }
  }, []);

  // Load ad settings and mid-roll cue points
  // Priority: Video-specific cue points > Global ad settings
  // Only fall back to global if video has NO cue points at all
  useEffect(() => {
    if (!enableAds) return;
    
    const loadAdSettings = async () => {
      try {
        let globalMidRolls = [];
        let globalVastTag = null;
        let globalPreRollEnabled = false;
        let globalPreRollCustomAd = null; // Store custom ad for pre-roll
        
        // Step 1: Fetch global ad settings (for fallback)
        const globalResponse = await fetch(`${API}/api/vod/admin/ads/settings`);
        if (globalResponse.ok) {
          const data = await globalResponse.json();
          console.log('[VODPlayer] Global ad settings:', data);
          
          // Store VAST tag — only if it's a real tag (not Google's test/sample tags)
          const isTestTag = data.google_vast_tag && (
            data.google_vast_tag.includes('single_ad_samples') ||
            data.google_vast_tag.includes('single_preroll_skippable') ||
            data.google_vast_tag.includes('vmap_pre_mid_post') ||
            data.google_vast_tag.includes('external/single_') ||
            data.google_vast_tag.includes('external/vmap_')
          );
          if (data.google_vast_tag && !isTestTag) {
            globalVastTag = data.google_vast_tag;
            setVastTag(data.google_vast_tag);
          } else if (isTestTag) {
            console.log('[VODPlayer] Ignoring test/sample VAST tag — not a real Google ad tag');
          }
          
          // Check if global pre-roll is enabled (ad_break_1 with time 0 or marked as pre-roll)
          // Ad Break 1 is typically pre-roll
          if (data.ad_break_1_enabled) {
            globalPreRollEnabled = true;
            
            // Use the /serve endpoint to determine which ad to play
            // This respects the ad_break_1_ad_id setting and rotation percentage
            try {
              const serveResponse = await fetch(`${API}/api/vod/admin/ads/serve?ad_break=1`);
              if (serveResponse.ok) {
                const serveData = await serveResponse.json();
                console.log('[VODPlayer] Ad serve response for pre-roll:', serveData);
                
                if (serveData.ad_type === 'custom' && serveData.ad) {
                  // Custom ad should be played
                  globalPreRollCustomAd = {
                    id: serveData.ad.id,
                    url: serveData.ad.video_url,
                    duration: serveData.ad.duration || 15,
                    skip_after: serveData.ad.skip_after ?? 5,
                    cta_enabled: serveData.ad.cta_enabled ?? false,
                    cta_text: serveData.ad.cta_text || 'Learn More',
                    click_url: serveData.ad.click_url || ''
                  };
                  console.log('[VODPlayer] Will play custom pre-roll ad:', serveData.ad.title);
                } else if (serveData.ad_type === 'google' && serveData.vast_tag) {
                  // Google ad — only if a real VAST tag is provided
                  console.log('[VODPlayer] Will play Google pre-roll ad');
                  setVastTag(serveData.vast_tag);
                } else {
                  // 'none' or no valid tag — skip ads entirely
                  console.log('[VODPlayer] No ads to serve (ad_type:', serveData.ad_type, ')');
                  globalPreRollEnabled = false;
                }
              }
            } catch (err) {
              console.log('[VODPlayer] Error calling serve endpoint:', err);
            }
          }
          
          // Store global pre-roll ad settings
          setGlobalPreRollAd(globalPreRollCustomAd);
          
          // Extract global ad break times (mid-rolls only, not pre-roll at 0)
          for (let i = 1; i <= 6; i++) {
            const enabled = data[`ad_break_${i}_enabled`];
            const time = data[`ad_break_${i}_time`];
            if (enabled && time !== undefined && time > 0) {
              globalMidRolls.push(time * 60); // Convert minutes to seconds
            }
          }
          console.log('[VODPlayer] Global pre-roll enabled:', globalPreRollEnabled);
          console.log('[VODPlayer] Global pre-roll custom ad:', globalPreRollCustomAd);
          console.log('[VODPlayer] Global mid-rolls loaded (in seconds):', globalMidRolls);
        }
        
        // Step 2: Check for video-specific cue points
        let videoSpecificCuePoints = []; // Full mid-roll objects with timestamp, ad_type, custom_url
        let videoPreRollEnabled = false;
        let videoPreRollSettings = null; // Store pre-roll ad type and custom URL
        let videoPostRollEnabled = false;
        let videoHasCustomSettings = false;
        let videoHasActiveAds = false; // NEW: Track if video has ANY active ad settings
        
        if (videoSlug) {
          try {
            const videoResponse = await fetch(`${API}/api/vod-admin/ad-cues/video/${videoSlug}/cue-points-for-player`);
            if (videoResponse.ok) {
              const videoData = await videoResponse.json();
              console.log('[VODPlayer] Video-specific ad settings:', videoData);
              
              // Check if video has custom ad settings in database
              videoHasCustomSettings = videoData.has_custom_settings === true;
              
              // Check pre-roll setting for this video
              // API returns null for disabled, or an object { ad_type: "...", custom_url: "..." } for enabled
              videoPreRollEnabled = videoData.pre_roll !== null;
              videoPreRollSettings = videoData.pre_roll;
              
              // Check post-roll setting
              videoPostRollEnabled = videoData.post_roll !== null;
              
              // Extract FULL mid-roll objects from video-specific settings (including ad_type, custom_url, skip_after, CTA)
              if (videoData.mid_rolls && Array.isArray(videoData.mid_rolls)) {
                videoSpecificCuePoints = videoData.mid_rolls
                  .filter(mr => mr.timestamp !== undefined && mr.timestamp > 0)
                  .map(mr => ({
                    timestamp: mr.timestamp,
                    ad_type: mr.ad_type || 'google',
                    custom_url: mr.custom_url || null,
                    custom_ad_id: mr.custom_ad_id || null,
                    duration: mr.duration || 15,
                    // Custom ad settings from the ad video
                    skip_after: mr.skip_after ?? 5,
                    cta_enabled: mr.cta_enabled ?? false,
                    cta_text: mr.cta_text || 'Learn More',
                    click_url: mr.click_url || ''
                  }));
              }
              
              // NEW: Check if video has ANY active ads
              // If pre-roll enabled OR has mid-roll cue points OR post-roll enabled → use video settings
              // If ALL are disabled/empty → fall back to global
              videoHasActiveAds = videoPreRollEnabled || videoSpecificCuePoints.length > 0 || videoPostRollEnabled;
              
              console.log('[VODPlayer] Video has custom settings:', videoHasCustomSettings);
              console.log('[VODPlayer] Video pre-roll enabled:', videoPreRollEnabled);
              console.log('[VODPlayer] Video pre-roll settings:', videoPreRollSettings);
              console.log('[VODPlayer] Video post-roll enabled:', videoPostRollEnabled);
              console.log('[VODPlayer] Video mid-roll cue points:', videoSpecificCuePoints);
              console.log('[VODPlayer] Video has ANY active ads:', videoHasActiveAds);
            }
          } catch (err) {
            console.log('[VODPlayer] Error fetching video cue points:', err);
          }
        }
        
        // Step 3: Decide which settings to use
        // RULE: If the video has ANY custom ad settings saved, use them exclusively.
        // This means once an admin configures video-specific ads (pre-roll, post-roll, 
        // mid-roll cue points, or any combination), global settings are completely ignored.
        let finalCuePoints = [];
        let shouldSkipPreRoll = false;
        
        if (videoHasCustomSettings) {
          // Video has custom ad settings — use ONLY video-specific settings, ignore global entirely
          finalCuePoints = videoSpecificCuePoints;
          setUseVideoSpecificAds(true);
          
          // Pre-roll follows video-specific setting (enabled/disabled)
          shouldSkipPreRoll = !videoPreRollEnabled;
          setSkipPreRoll(shouldSkipPreRoll);
          
          // If video has custom pre-roll with a specific ad, use it instead of global
          if (videoPreRollEnabled && videoPreRollSettings) {
            const preRollUrl = videoPreRollSettings.custom_url;
            if (preRollUrl) {
              setGlobalPreRollAd({
                id: videoPreRollSettings.custom_ad_id || null,
                url: preRollUrl,
                duration: videoPreRollSettings.duration || 15,
                skip_after: videoPreRollSettings.skip_after ?? 5,
                cta_enabled: videoPreRollSettings.cta_enabled ?? false,
                cta_text: videoPreRollSettings.cta_text || 'Learn More',
                click_url: videoPreRollSettings.click_url || ''
              });
            }
            // If no custom URL but pre-roll enabled, keep globalPreRollAd as-is (Google ad)
          } else if (!videoPreRollEnabled) {
            // Pre-roll explicitly disabled for this video — clear it
            setGlobalPreRollAd(null);
          }
          
          console.log('[VODPlayer] ✓ Using VIDEO-SPECIFIC settings (overrides global)');
          console.log('[VODPlayer] ✓ Pre-roll enabled:', videoPreRollEnabled);
          console.log('[VODPlayer] ✓ Mid-roll cue points:', finalCuePoints.length);
          console.log('[VODPlayer] ✓ Post-roll enabled:', videoPostRollEnabled);
        } else {
          // No video-specific settings → use global ad settings
          finalCuePoints = globalMidRolls.map(time => ({
            timestamp: time,
            ad_type: 'google',
            custom_url: null
          }));
          setUseVideoSpecificAds(false);
          setSkipPreRoll(!globalPreRollEnabled);
          
          console.log('[VODPlayer] ✓ Using GLOBAL ad settings (no video-specific settings found)');
          console.log('[VODPlayer] ✓ Global pre-roll enabled:', globalPreRollEnabled);
          console.log('[VODPlayer] ✓ Global mid-roll cue points:', finalCuePoints.length);
        }
        
        // Set the final mid-roll cue points (now full objects, sorted by timestamp)
        setMidRollCuePoints(finalCuePoints.length > 0 ? finalCuePoints.sort((a, b) => a.timestamp - b.timestamp) : []);
        
        // Mark ad settings as loaded
        setAdSettingsLoaded(true);
        console.log('[VODPlayer] ✓ Ad settings loaded');
        console.log('[VODPlayer] ✓ Final cue points set:', finalCuePoints);
      } catch (err) {
        console.log('[VODPlayer] Error loading ad settings:', err);
        setAdSettingsLoaded(true); // Still mark as loaded so we don't block forever
      }
    };
    
    loadAdSettings();
  }, [enableAds, videoSlug]);

  // Load IMA SDK
  useEffect(() => {
    if (!enableAds) return;
    
    if (window.google?.ima) {
      setImaReady(true);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
    script.async = true;
    script.onload = () => {
      console.log('[VODPlayer] IMA SDK loaded');
      setImaReady(true);
    };
    document.head.appendChild(script);
  }, [enableAds]);

  // Initialize IMA
  const initializeIMA = useCallback(() => {
    if (!imaReady || !adVideoRef.current || !adContainerRef.current || adsLoaderRef.current) return;
    
    try {
      // Use dedicated ad video element for proper mobile support
      adDisplayContainerRef.current = new window.google.ima.AdDisplayContainer(
        adContainerRef.current,
        adVideoRef.current  // Use dedicated ad video, not content video
      );
      
      adsLoaderRef.current = new window.google.ima.AdsLoader(adDisplayContainerRef.current);
      
      adsLoaderRef.current.addEventListener(
        window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
        onAdsManagerLoaded
      );
      
      adsLoaderRef.current.addEventListener(
        window.google.ima.AdErrorEvent.Type.AD_ERROR,
        onAdErrorHandler
      );
      
      console.log('[VODPlayer] IMA initialized with dedicated ad video element');
    } catch (err) {
      console.error('[VODPlayer] IMA init error:', err);
    }
  }, [imaReady]);

  // Initialize IMA when ready
  useEffect(() => {
    if (imaReady && adVideoRef.current && adContainerRef.current) {
      initializeIMA();
    }
  }, [imaReady, initializeIMA]);

  // Ads Manager loaded
  const onAdsManagerLoaded = useCallback((event) => {
    console.log('[VODPlayer] Ads manager loaded');
    
    const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
    
    // Use adVideoRef for ad playback instead of content video
    adsManagerRef.current = event.getAdsManager(adVideoRef.current, adsRenderingSettings);
    const am = adsManagerRef.current;
    
    // Pause content when ad starts
    am.addEventListener(window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, () => {
      console.log('[VODPlayer] Ad starting');
      videoRef.current?.pause();
      setPlayerState('ad-playing');
      onAdStart?.();
      
      // Track ad impression for creator revenue — only for real Google/VAST ads (not test ads)
      if ((videoId || videoSlug) && vastTag) {
        fetch(`${API}/api/vod/admin/ads/track-impression`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            video_id: videoId || videoSlug,
            ad_type: 'vast',
            ad_position: midRollInProgressRef.current ? 'mid-roll' : 'pre-roll'
          })
        }).catch(err => console.log('[VODPlayer] Ad impression track error:', err));
      }
    });
    
    // Resume content when ad ends
    am.addEventListener(window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, () => {
      console.log('[VODPlayer] Ad ended, resuming content');
      // Reset mid-roll in progress flag
      midRollInProgressRef.current = false;
      setPlayerState('playing');
      setAdRequested(true); // Mark ad as completed
      // Small delay to ensure state is updated before playing
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.log('[VODPlayer] Auto-play after ad blocked:', err);
          });
        }
      }, 100);
      onAdEnd?.();
    });
    
    // All ads completed
    am.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
      console.log('[VODPlayer] All ads completed');
      // Reset mid-roll in progress flag
      midRollInProgressRef.current = false;
      setPlayerState('playing');
      setAdRequested(true);
      // Ensure content plays after all ads
      setTimeout(() => {
        if (videoRef.current && videoRef.current.paused) {
          videoRef.current.play().catch(err => {
            console.log('[VODPlayer] Auto-play after all ads blocked:', err);
          });
        }
      }, 100);
    });
    
    // Ad completed (single ad)
    am.addEventListener(window.google.ima.AdEvent.Type.COMPLETE, () => {
      console.log('[VODPlayer] Single ad completed');
      // Reset mid-roll in progress flag
      midRollInProgressRef.current = false;
    });
    
    // Ad skipped
    am.addEventListener(window.google.ima.AdEvent.Type.SKIPPED, () => {
      console.log('[VODPlayer] Ad skipped');
      // Reset mid-roll in progress flag
      midRollInProgressRef.current = false;
      setPlayerState('playing');
      setAdRequested(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.log('[VODPlayer] Auto-play after skip blocked:', err);
          });
        }
      }, 100);
      onAdSkip?.();
      onAdEnd?.();
    });
    
    // Ad error
    am.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdErrorHandler);
    
    // Start the ad
    try {
      const width = containerRef.current?.clientWidth || 640;
      const height = containerRef.current?.clientHeight || 360;
      am.init(width, height, window.google.ima.ViewMode.NORMAL);
      am.start();
    } catch (err) {
      console.error('[VODPlayer] Ad start error:', err);
      midRollInProgressRef.current = false;
      playContent();
    }
  }, [onAdStart, onAdEnd, onAdSkip]);

  // Ad error - play content
  const onAdErrorHandler = useCallback((event) => {
    console.log('[VODPlayer] Ad error:', event?.getError?.()?.toString());
    
    // Clean up ads manager
    if (adsManagerRef.current) {
      try {
        adsManagerRef.current.destroy();
      } catch (e) {
        console.log('[VODPlayer] Error destroying ads manager:', e);
      }
      adsManagerRef.current = null;
    }
    
    // Reset mid-roll in progress flag so future mid-rolls can trigger
    midRollInProgressRef.current = false;
    
    // Reset state and play content
    setPlayerState('playing');
    setAdRequested(true);
    
    // Play content video
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(err => {
          console.log('[VODPlayer] Auto-play after ad error blocked:', err);
        });
      }
    }, 100);
    
    onAdEnd?.();
  }, [onAdEnd]);

  // Play the actual video content (must be defined before requestAds which references it)
  const playContent = useCallback(() => {
    if (!videoRef.current) return;
    
    console.log('[VODPlayer] Playing content');
    setPlayerState('loading');
    
    videoRef.current.play()
      .then(() => {
        console.log('[VODPlayer] Content playing');
        setPlayerState('playing');
        onPlay?.();
      })
      .catch(err => {
        console.error('[VODPlayer] Play error:', err);
        setPlayerState('paused');
      });
  }, [onPlay]);

  // Request ads
  const requestAds = useCallback(() => {
    if (!adsLoaderRef.current || !adDisplayContainerRef.current || !vastTag) {
      console.log('[VODPlayer] IMA not ready or no VAST tag configured — skipping Google ad request');
      playContent();
      return;
    }
    
    adDisplayContainerRef.current.initialize();
    
    const adsRequest = new window.google.ima.AdsRequest();
    const separator = vastTag.includes('?') ? '&' : '?';
    adsRequest.adTagUrl = `${vastTag}${separator}correlator=${Date.now()}`;
    
    const width = containerRef.current?.clientWidth || 640;
    const height = containerRef.current?.clientHeight || 360;
    adsRequest.linearAdSlotWidth = width;
    adsRequest.linearAdSlotHeight = height;
    
    console.log('[VODPlayer] Requesting Google VAST ads with tag:', vastTag.substring(0, 60));
    setAdRequested(true);
    adsLoaderRef.current.requestAds(adsRequest);
  }, [vastTag, playContent]);

  // Track custom ad events (impression, complete, skip) to ad_analytics_events
  // Always sends tracking — uses ad_url as fallback if adId is null
  const trackCustomAdEvent = useCallback((adId, eventType, extraData = {}) => {
    const adUrl = extraData.ad_url;
    if (!adId && !adUrl) return;
    const payload = {
      event_type: eventType,
      video_id: videoId || videoSlug,
      ...extraData
    };
    if (adId) payload.ad_id = adId;
    if (adUrl) payload.ad_url = adUrl;
    fetch(`${API}/api/vod/admin/ads/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).catch(err => console.log('[VODPlayer] Custom ad track error:', err));
  }, [videoId, videoSlug]);

  // Play content
  // Helper: Play a custom ad on the correct video element (handles iOS fullscreen)
  const playCustomAdVideo = useCallback((adUrl, onEnded, onTimeUpdateCb) => {
    const useMainVideo = isVideoElementFullscreen.current;
    const targetVideo = useMainVideo ? videoRef.current : adVideoRef.current;
    
    if (!targetVideo) return false;
    
    console.log(`[VODPlayer] Playing custom ad on ${useMainVideo ? 'MAIN video (iOS fullscreen)' : 'ad video element'}`);
    
    isCustomAdPlaying.current = true;
    usingMainVideoForAd.current = useMainVideo;
    
    // If using main video, save its current state to restore later
    if (useMainVideo && videoRef.current) {
      savedVideoState.current = {
        src: videoRef.current.currentSrc || videoRef.current.src || '',
        currentTime: videoRef.current.currentTime || 0,
      };
      videoRef.current.pause();
    }
    
    // Clear stale listeners
    targetVideo.onended = null;
    targetVideo.ontimeupdate = null;
    
    targetVideo.src = adUrl;
    targetVideo.load();
    targetVideo.play().catch(err => {
      console.log('[VODPlayer] Custom ad play error:', err);
      isCustomAdPlaying.current = false;
      usingMainVideoForAd.current = false;
      onEnded?.(true); // Pass error flag
      return;
    });
    
    targetVideo.onended = () => {
      console.log('[VODPlayer] Custom ad ended');
      isCustomAdPlaying.current = false;
      usingMainVideoForAd.current = false;
      
      // Clean up ad video
      targetVideo.onended = null;
      targetVideo.ontimeupdate = null;
      targetVideo.pause();
      
      // If we used the main video, restore its original source and position
      if (useMainVideo && videoRef.current) {
        videoRef.current.src = savedVideoState.current.src;
        videoRef.current.load();
        videoRef.current.currentTime = savedVideoState.current.currentTime;
      } else {
        targetVideo.removeAttribute('src');
        targetVideo.load();
      }
      
      onEnded?.(false);
    };
    
    targetVideo.ontimeupdate = () => {
      if (targetVideo) {
        onTimeUpdateCb?.(targetVideo.currentTime);
      }
    };
    
    return true;
  }, []);

  // Handle play click - clear paused frame when resuming
  const handlePlay = useCallback(() => {
    console.log('[VODPlayer] Play clicked, state:', playerState, 'skipPreRoll:', skipPreRoll, 'adSettingsLoaded:', adSettingsLoaded);
    
    // Clear the paused frame when resuming
    setPausedFrame(null);
    
    if (playerState === 'ad-playing') return;
    
    // Wait for ad settings to load before deciding on pre-roll
    if (enableAds && !adSettingsLoaded) {
      console.log('[VODPlayer] Waiting for ad settings to load...');
      setPlayerState('loading');
      return; // Will be triggered again by auto-play effect when settings load
    }
    
    // Check if we should play pre-roll ad
    // Skip pre-roll if: using video-specific settings AND pre-roll is disabled for this video
    if (enableAds && imaReady && !adRequested) {
      if (skipPreRoll) {
        // Video has custom settings with pre-roll disabled - skip to content
        console.log('[VODPlayer] ✓ Skipping pre-roll (disabled for this video)');
        setAdRequested(true); // Mark as requested so we don't try again
        playContent();
      } else if (globalPreRollAd && globalPreRollAd.url) {
        // Play custom pre-roll ad from global settings
        console.log('[VODPlayer] Playing CUSTOM pre-roll ad:', globalPreRollAd);
        setAdRequested(true);
        setPlayerState('ad-playing');
        
        // Track custom ad impression
        trackCustomAdEvent(globalPreRollAd.id, 'impression', { ad_break: 1, ad_url: globalPreRollAd.url });
        trackCustomAdEvent(globalPreRollAd.id, 'start', { ad_break: 1, ad_url: globalPreRollAd.url });
        
        // Set current custom ad with all settings for CTA/skip buttons
        setCurrentCustomAd({
          id: globalPreRollAd.id,
          url: globalPreRollAd.url,
          duration: globalPreRollAd.duration || 15,
          skip_after: globalPreRollAd.skip_after ?? 5,
          cta_enabled: globalPreRollAd.cta_enabled ?? false,
          cta_text: globalPreRollAd.cta_text || 'Learn More',
          click_url: globalPreRollAd.click_url || ''
        });
        setCustomAdTime(0);
        
        // Play custom ad video using the helper (handles iOS fullscreen)
        const adStarted = playCustomAdVideo(
          globalPreRollAd.url,
          (wasError) => {
            // onEnded callback
            if (wasError) {
              setCurrentCustomAd(null);
              playContent();
              return;
            }
            // Track completion
            trackCustomAdEvent(globalPreRollAd.id, 'complete', { ad_url: globalPreRollAd.url });
            setCurrentCustomAd(null);
            setCustomAdTime(0);
            setPlayerState('playing');
            setTimeout(() => {
              if (videoRef.current) {
                videoRef.current.play().catch(() => {});
              }
            }, 200);
            onAdEnd?.();
          },
          (time) => setCustomAdTime(time) // onTimeUpdate
        );
        
        if (!adStarted) {
          // Fallback: couldn't play ad, skip to content
          setCurrentCustomAd(null);
          playContent();
        } else {
          onAdStart?.();
        }
      } else {
        // Play Google IMA pre-roll ad
        console.log('[VODPlayer] Playing GOOGLE pre-roll ad');
        setPlayerState('loading');
        requestAds();
      }
    } else {
      playContent();
    }
  }, [playerState, enableAds, imaReady, adRequested, skipPreRoll, adSettingsLoaded, requestAds, playContent, globalPreRollAd, onAdStart, onAdEnd, trackCustomAdEvent]);

  // Handle pause - force frame refresh for HLS streams
  const handlePause = useCallback(() => {
    // Don't interfere with custom ad playback
    if (isCustomAdPlaying.current) return;
    if (playerState === 'ad-playing' && adsManagerRef.current) {
      adsManagerRef.current.pause();
    } else if (videoRef.current) {
      const video = videoRef.current;
      const currentPos = video.currentTime;
      
      // Pause the video
      video.pause();
      setPlayerState('paused');
      
      // For HLS streams, force a frame refresh by seeking to current position
      // This helps maintain the video frame on screen
      if (isHls && currentPos > 0) {
        // Small seek to refresh the frame buffer
        setTimeout(() => {
          if (videoRef.current && videoRef.current.paused) {
            videoRef.current.currentTime = currentPos;
          }
        }, 50);
      }
      
      onPause?.();
    }
  }, [playerState, onPause, isHls]);

  // AUTO-PLAY: Trigger playback automatically when component mounts and is ready
  useEffect(() => {
    if (!autoPlay || autoPlayTriggeredRef.current) return;
    
    // Wait for video element, IMA, AND ad settings to be ready
    const isReady = videoRef.current && (imaReady || !enableAds) && (adSettingsLoaded || !enableAds);
    
    if (isReady && playerState === 'idle') {
      console.log('[VODPlayer] Auto-play triggered, adSettingsLoaded:', adSettingsLoaded, 'skipPreRoll:', skipPreRoll);
      autoPlayTriggeredRef.current = true;
      
      // Small delay to ensure everything is mounted
      setTimeout(() => {
        handlePlay();
      }, 100);
    }
  }, [autoPlay, imaReady, enableAds, playerState, adSettingsLoaded, skipPreRoll, handlePlay]);

  // RETRY PLAY: When ad settings finish loading while player is stuck in 'loading' state
  // This handles the case where play() was called before ad settings loaded (e.g. resume flow)
  // Only retry if no ad has been requested yet (avoid premature content play during ad load)
  useEffect(() => {
    if (adSettingsLoaded && playerState === 'loading' && !adRequested) {
      console.log('[VODPlayer] Ad settings now loaded, retrying play...');
      handlePlay();
    }
  }, [adSettingsLoaded, playerState, adRequested, handlePlay]);
  
  // Safety timeout: if stuck in 'loading' state for >8 seconds, force play content
  useEffect(() => {
    if (playerState !== 'loading') return;
    const safetyTimer = setTimeout(() => {
      if (playerState === 'loading') {
        console.warn('[VODPlayer] Safety timeout: stuck in loading state, forcing content play');
        playContent();
      }
    }, 8000);
    return () => clearTimeout(safetyTimer);
  }, [playerState, playContent]);

  // Video events
  const handleTimeUpdate = useCallback(() => {
    // Don't process during any custom ad playback
    if (isCustomAdPlaying.current || midRollInProgressRef.current) return;
    if (videoRef.current && playerState !== 'ad-playing') {
      const time = videoRef.current.currentTime;
      setCurrentTime(time);
      setDuration(videoRef.current.duration);
      onTimeUpdate?.(time, videoRef.current.duration);
      
      // Check for mid-roll ads
      // Use ref to prevent double-triggering while async state updates
      // midRollCuePoints now contains objects: { timestamp, ad_type, custom_url }
      if (enableAds && midRollCuePoints.length > 0 && time > 5 && !midRollInProgressRef.current) {
        for (const cuePoint of midRollCuePoints) {
          const cueTime = cuePoint.timestamp;
          // Check if we've passed the cue point (within 2 second window) and haven't played it
          if (time >= cueTime && time < cueTime + 2 && !playedMidRolls.has(cueTime)) {
            // IMMEDIATELY set ref to prevent double-trigger (sync operation)
            midRollInProgressRef.current = true;
            
            console.log(`[VODPlayer] Mid-roll ad triggered at ${cueTime}s (current: ${Math.floor(time)}s)`);
            console.log(`[VODPlayer] Ad type: ${cuePoint.ad_type}, Custom URL: ${cuePoint.custom_url}`);
            
            // Mark this cue point as played
            setPlayedMidRolls(prev => new Set([...prev, cueTime]));
            
            // Pause video and play mid-roll ad
            videoRef.current.pause();
            setPlayerState('loading');
            
            // Request mid-roll ad with full cue point object (includes ad_type and custom_url)
            requestMidRollAd(cuePoint);
            break;
          }
        }
      }
    }
  }, [playerState, onTimeUpdate, enableAds, midRollCuePoints, playedMidRolls]);

  // Request mid-roll ad - handles both Google (VAST) and Custom ads
  const requestMidRollAd = useCallback(async (cuePoint) => {
    const { timestamp, ad_type, custom_url, duration, custom_ad_id } = cuePoint;
    console.log('[VODPlayer] Requesting mid-roll ad for cue:', timestamp, 'type:', ad_type);
    console.log('[VODPlayer] Cue point data:', cuePoint);
    
    // Handle CUSTOM AD - play a custom video
    if (ad_type === 'custom' && (custom_url || custom_ad_id)) {
      console.log('[VODPlayer] Playing CUSTOM mid-roll ad');
      
      // Track custom ad impression and start
      trackCustomAdEvent(custom_ad_id, 'impression', { ad_break: timestamp, ad_url: custom_url });
      trackCustomAdEvent(custom_ad_id, 'start', { ad_break: timestamp, ad_url: custom_url });
      
      // Use ad details directly from the cue point (resolved by backend)
      const adDetails = {
        id: custom_ad_id,
        url: custom_url,
        duration: duration || 15,
        skip_after: cuePoint.skip_after ?? 5,
        cta_enabled: cuePoint.cta_enabled ?? false,
        cta_text: cuePoint.cta_text || 'Learn More',
        click_url: cuePoint.click_url || ''
      };
      
      console.log('[VODPlayer] Ad details from cue point:', adDetails);
      
      // Store ALL custom ad data including CTA settings
      setCurrentCustomAd(adDetails);
      setCustomAdTime(0);
      setPlayerState('ad-playing');
      
      // Pause main video explicitly
      if (videoRef.current) {
        videoRef.current.pause();
      }
      
      // Play custom ad using the helper (handles iOS fullscreen)
      const adStarted = playCustomAdVideo(
        adDetails.url,
        (wasError) => {
          if (wasError) {
            setCurrentCustomAd(null);
            midRollInProgressRef.current = false;
            playContent();
            return;
          }
          // Track completion
          trackCustomAdEvent(custom_ad_id, 'complete', { ad_url: custom_url });
          setCurrentCustomAd(null);
          setCustomAdTime(0);
          midRollInProgressRef.current = false;
          setPlayerState('playing');
          setTimeout(() => {
            if (videoRef.current) {
              videoRef.current.play().catch(err => {
                console.log('[VODPlayer] Auto-play after custom ad blocked:', err);
              });
            }
          }, 200);
          onAdEnd?.();
        },
        (time) => setCustomAdTime(time)
      );
      
      if (!adStarted) {
        setCurrentCustomAd(null);
        midRollInProgressRef.current = false;
        playContent();
      } else {
        onAdStart?.();
      }
      return;
    }
    
    // Handle GOOGLE AD (VAST)
    console.log('[VODPlayer] Playing GOOGLE ad via VAST');
    
    // Destroy existing ads manager if any
    if (adsManagerRef.current) {
      try {
        adsManagerRef.current.destroy();
      } catch (e) {
        console.log('[VODPlayer] Error destroying ads manager:', e);
      }
      adsManagerRef.current = null;
    }
    
    if (!adsLoaderRef.current || !vastTag || !imaReady) {
      console.log('[VODPlayer] No ads loader/VAST tag for mid-roll, resuming content');
      midRollInProgressRef.current = false;
      playContent();
      return;
    }
    
    // Re-initialize ad display container for mid-roll
    try {
      adDisplayContainerRef.current?.initialize();
    } catch (e) {
      console.log('[VODPlayer] Ad display container already initialized');
    }
    
    const adsRequest = new window.google.ima.AdsRequest();
    const midSeparator = vastTag.includes('?') ? '&' : '?';
    adsRequest.adTagUrl = `${vastTag}${midSeparator}correlator=${Date.now()}&pod=${timestamp}`;
    
    const width = containerRef.current?.clientWidth || 640;
    const height = containerRef.current?.clientHeight || 360;
    adsRequest.linearAdSlotWidth = width;
    adsRequest.linearAdSlotHeight = height;
    
    console.log('[VODPlayer] Mid-roll VAST request URL:', adsRequest.adTagUrl);
    
    try {
      adsLoaderRef.current.requestAds(adsRequest);
    } catch (e) {
      console.error('[VODPlayer] Error requesting mid-roll ads:', e);
      midRollInProgressRef.current = false;
      playContent();
    }
  }, [vastTag, imaReady, playContent, onAdStart, onAdEnd, trackCustomAdEvent, playCustomAdVideo]);

  const handleVideoEnded = useCallback(() => {
    setPlayerState('idle');
    onEnded?.();
  }, [onEnded]);

  // Resize handler — also re-sizes IMA ads for fullscreen transitions
  useEffect(() => {
    const handleResize = () => {
      if (adsManagerRef.current && containerRef.current) {
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        const viewMode = isLandscapeFullscreen 
          ? window.google.ima.ViewMode.FULLSCREEN 
          : window.google.ima.ViewMode.NORMAL;
        adsManagerRef.current.resize(width, height, viewMode);
      }
    };
    window.addEventListener('resize', handleResize);
    // Also resize when landscape fullscreen state changes
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [isLandscapeFullscreen]);

  // Expose methods
  useImperativeHandle(ref, () => ({
    play: () => { handlePlay(); return Promise.resolve(); },
    pause: handlePause,
    seek: (time) => { if (videoRef.current) videoRef.current.currentTime = time; },
    getCurrentTime: () => currentTime,
    // Live getters — read directly from the native DOM element, never stale
    get currentTime() { return videoRef.current ? videoRef.current.currentTime : 0; },
    getDuration: () => duration,
    get duration() { return videoRef.current ? videoRef.current.duration || 0 : 0; },
    getState: () => playerState,
    setQuality: handleQualityChange,
    getQuality: () => currentQuality,
  }), [handlePlay, handlePause, currentTime, duration, playerState, handleQualityChange, currentQuality]);

  // Format time
  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const hrs = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    if (hrs > 0) {
      return `${hrs}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  
  // Get quality label — always shows actual resolution, never "Auto"
  const getQualityLabel = () => {
    if (currentQuality === '4k') return '4K';
    if (currentQuality === 'hd') return '1080p';
    if (currentQuality === 'sd') return 'SD';
    // HLS level label
    const match = availableQualities.find(q => q.id === currentQuality);
    if (match) return match.label;
    // Fallback: show highest available
    if (src4k) return '4K';
    if (srcHd) return '1080p';
    return 'SD';
  };

  const isIdle = playerState === 'idle';
  const isPlaying = playerState === 'playing';
  const isPaused = playerState === 'paused';
  const isAdPlaying = playerState === 'ad-playing';
  const isLoading = playerState === 'loading';

  // Keyboard controls - spacebar to toggle play/pause
  const handleKeyDown = useCallback((e) => {
    // Only handle if the player container is focused or video is playing
    if (e.key === ' ' || e.code === 'Space') {
      e.preventDefault();
      e.stopPropagation();
      if (!isAdPlaying) {
        if (isPlaying) {
          handlePause();
        } else {
          handlePlay();
        }
      }
    }
    // Arrow keys for seeking
    if (e.key === 'ArrowRight' && videoRef.current) {
      e.preventDefault();
      videoRef.current.currentTime = Math.min(videoRef.current.currentTime + 10, duration);
    }
    if (e.key === 'ArrowLeft' && videoRef.current) {
      e.preventDefault();
      videoRef.current.currentTime = Math.max(videoRef.current.currentTime - 10, 0);
    }
    // F for fullscreen
    if (e.key === 'f' || e.key === 'F') {
      e.preventDefault();
      const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
      if (isFullscreen) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } else {
        const videoEl = videoRef.current;
        const container = containerRef.current;
        isVideoElementFullscreen.current = false;
        if (container?.requestFullscreen) container.requestFullscreen().catch(() => {
          if (videoEl?.webkitEnterFullscreen) { isVideoElementFullscreen.current = true; videoEl.webkitEnterFullscreen(); }
        });
        else if (container?.webkitRequestFullscreen) container.webkitRequestFullscreen();
        else if (videoEl?.webkitEnterFullscreen) { isVideoElementFullscreen.current = true; videoEl.webkitEnterFullscreen(); }
      }
    }
    // M for mute
    if (e.key === 'm' || e.key === 'M') {
      e.preventDefault();
      if (videoRef.current) {
        videoRef.current.muted = !videoRef.current.muted;
      }
    }
  }, [isAdPlaying, isPlaying, handlePause, handlePlay, duration]);

  // Add global keyboard listener when video is playing/paused
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Only handle spacebar if not focused on input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if ((isPlaying || isPaused) && (e.key === ' ' || e.code === 'Space')) {
        e.preventDefault();
        if (!isAdPlaying) {
          if (isPlaying) {
            handlePause();
          } else {
            handlePlay();
          }
        }
      }
    };
    
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isPlaying, isPaused, isAdPlaying, handlePause, handlePlay]);

  return (
    <div 
      ref={containerRef}
      className={`vod-player ${className}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        backgroundColor: '#000',
        outline: 'none',
        ...style 
      }}
      onClick={(e) => {
        setShowQualityMenu(false);
        // In landscape fullscreen, tapping anywhere on the container shows controls
        if (isLandscapeFullscreen && !isAdPlaying) {
          showMobileControlsBriefly();
        }
      }}
    >
      {/* Video Element - Always visible, never hidden when paused */}
      <video
        ref={videoRef}
        src={isHls ? undefined : getCurrentSrc()}
        poster={poster}
        playsInline
        webkit-playsinline="true"
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleVideoEnded}
        onError={(e) => onError?.(e)}
        onClick={(e) => {
          e.stopPropagation();
          if (isLandscapeFullscreen) {
            // In landscape fullscreen: tap toggles controls visibility
            if (!isAdPlaying) {
              if (mobileControlsVisible) {
                // Controls are visible — tap toggles play/pause
                isPlaying ? handlePause() : handlePlay();
              }
              // Always show controls on tap (with auto-hide)
              showMobileControlsBriefly();
            }
          } else {
            // Normal mode: tap toggles play/pause
            if (!isAdPlaying) {
              isPlaying ? handlePause() : handlePlay();
            }
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: (isAdPlaying && !usingMainVideoForAd.current) ? 'none' : 'block',
          cursor: 'pointer',
          position: 'relative',
          zIndex: 1,
        }}
      />
      
      {/* Paused Frame Image - Shows captured frame when video is paused */}
      {isPaused && pausedFrame && !isAdPlaying && (
        <img
          src={pausedFrame}
          alt="Paused frame"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            zIndex: 5,
            pointerEvents: 'none',
          }}
        />
      )}
      
      {/* Dedicated Ad Video Element - Required for mobile IMA ads */}
      <video
        ref={adVideoRef}
        playsInline
        webkit-playsinline="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: (isAdPlaying && !usingMainVideoForAd.current) ? 'block' : 'none',
          zIndex: 9,
          backgroundColor: '#000',
        }}
      />
      
      {/* Ad Container - IMA renders controls here (hidden during custom ads) */}
      <div
        ref={adContainerRef}
        className="ad-container-landscape"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: (isAdPlaying && !currentCustomAd) ? 'block' : 'none',
          zIndex: 10,
        }}
      />
      
      {/* Loading Spinner */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 20,
        }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
        </div>
      )}
      
      {/* Play Button - Only when idle */}
      {isIdle && !isLoading && (
        <div
          onClick={handlePlay}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            cursor: 'pointer',
            zIndex: 30,
          }}
        >
          <div style={{
            width: 80,
            height: 80,
            backgroundColor: 'rgba(0,0,0,0.7)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s',
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
      )}
      
      {/* Ad Badge */}
      {isAdPlaying && (
        <div style={{
          position: 'absolute',
          top: 16,
          left: 16,
          backgroundColor: '#f59e0b',
          color: '#000',
          padding: '4px 12px',
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 'bold',
          zIndex: 30,
        }}>
          {currentCustomAd ? 'Custom Ad' : 'Ad'}
        </div>
      )}
      
      {/* Skip Custom Ad Button - Only show after skip_after seconds (0 = not skippable) */}
      {isAdPlaying && currentCustomAd && currentCustomAd.skip_after > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Only allow skip if we've passed the skip_after time
            if (customAdTime >= currentCustomAd.skip_after) {
              console.log('[VODPlayer] Skip custom ad clicked');
              // Track skip event
              trackCustomAdEvent(currentCustomAd.id, 'skip', { skip_time: customAdTime });
              isCustomAdPlaying.current = false;
              
              // Stop custom ad on whichever element it's playing on
              if (usingMainVideoForAd.current && videoRef.current) {
                // Ad was playing on main video (iOS fullscreen) - restore original source
                videoRef.current.onended = null;
                videoRef.current.ontimeupdate = null;
                videoRef.current.pause();
                videoRef.current.src = savedVideoState.current.src;
                videoRef.current.load();
                videoRef.current.currentTime = savedVideoState.current.currentTime;
                usingMainVideoForAd.current = false;
              } else if (adVideoRef.current) {
                adVideoRef.current.onended = null;
                adVideoRef.current.ontimeupdate = null;
                adVideoRef.current.pause();
                adVideoRef.current.removeAttribute('src');
                adVideoRef.current.load();
              }
              setCurrentCustomAd(null);
              setCustomAdTime(0);
              midRollInProgressRef.current = false;
              setPlayerState('playing');
              // Resume content
              setTimeout(() => {
                if (videoRef.current) {
                  videoRef.current.play().catch(() => {});
                }
              }, 200);
              onAdSkip?.();
              onAdEnd?.();
            }
          }}
          style={{
            position: 'absolute',
            bottom: window.innerWidth < 640 ? 12 : 80,
            right: 16,
            backgroundColor: customAdTime >= currentCustomAd.skip_after ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.5)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
            padding: '8px 16px',
            borderRadius: 4,
            fontSize: 14,
            cursor: customAdTime >= currentCustomAd.skip_after ? 'pointer' : 'default',
            zIndex: 30,
            opacity: customAdTime >= currentCustomAd.skip_after ? 1 : 0.7,
          }}
          data-testid="skip-custom-ad-button"
        >
          {customAdTime >= currentCustomAd.skip_after 
            ? 'Skip Ad →' 
            : `Skip in ${Math.ceil(currentCustomAd.skip_after - customAdTime)}s`
          }
        </button>
      )}
      
      {/* CTA Button for Custom Ad - Show if cta_enabled is true */}
      {isAdPlaying && currentCustomAd && currentCustomAd.cta_enabled && (
        currentCustomAd.click_url ? (
        <a
          href={(() => {
            try {
              const u = new URL(currentCustomAd.click_url);
              u.searchParams.set('utm_source', 'ltv_ad');
              u.searchParams.set('utm_medium', 'video_ad');
              u.searchParams.set('utm_campaign', (currentCustomAd.id || 'custom_ad').substring(0, 24));
              return u.toString();
            } catch { return currentCustomAd.click_url; }
          })()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            console.log('[VODPlayer] CTA clicked:', currentCustomAd.click_url);
            trackCustomAdEvent(currentCustomAd.id, 'click', { ad_url: currentCustomAd.url });
          }}
          style={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            backgroundColor: '#8b5cf6',
            color: '#fff',
            padding: window.innerWidth < 640 ? '6px 12px' : '10px 18px',
            borderRadius: 6,
            fontSize: window.innerWidth < 640 ? 12 : 14,
            fontWeight: '600',
            textDecoration: 'none',
            cursor: 'pointer',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)',
            lineHeight: 1,
          }}
          data-testid="cta-button"
        >
          {currentCustomAd.cta_text || 'Learn More'}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
          </svg>
        </a>
        ) : (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            backgroundColor: '#8b5cf6',
            color: '#fff',
            padding: window.innerWidth < 640 ? '6px 12px' : '10px 18px',
            borderRadius: 6,
            fontSize: window.innerWidth < 640 ? 12 : 14,
            fontWeight: '600',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.4)',
            lineHeight: 1,
          }}
          data-testid="cta-button"
        >
          {currentCustomAd.cta_text || 'Learn More'}
        </div>
        )
      )}
      
      {/* Paused Play Button Overlay - Shows when paused, auto-hide in landscape fullscreen */}
      {isPaused && !isAdPlaying && (
        <div
          onClick={(e) => { e.stopPropagation(); handlePlay(); if (isLandscapeFullscreen) showMobileControlsBriefly(); }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            cursor: 'pointer',
            zIndex: 25,
            opacity: shouldShowControls ? 1 : 0,
            pointerEvents: shouldShowControls ? 'auto' : 'none',
            transition: 'opacity 0.3s ease',
          }}
        >
          <div style={{
            width: 70,
            height: 70,
            backgroundColor: 'rgba(0,0,0,0.6)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
      )}
      
      {/* Controls - Show when playing or paused, auto-hide in landscape fullscreen */}
      {(isPlaying || isPaused) && !isAdPlaying && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
          padding: '24px 16px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          zIndex: 20,
          opacity: shouldShowControls ? 1 : 0,
          pointerEvents: shouldShowControls ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }}>
          {/* Play/Pause */}
          <button
            onClick={(e) => { e.stopPropagation(); isPlaying ? handlePause() : handlePlay(); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>
          
          {/* Time */}
          <span style={{ color: '#fff', fontSize: 13, minWidth: 80 }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
          
          {/* Progress Bar */}
          <div 
            style={{
              flex: 1,
              height: 4,
              backgroundColor: 'rgba(255,255,255,0.3)',
              borderRadius: 2,
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (videoRef.current && duration) {
                const rect = e.currentTarget.getBoundingClientRect();
                const percent = (e.clientX - rect.left) / rect.width;
                videoRef.current.currentTime = percent * duration;
              }
            }}
          >
            <div style={{
              width: `${(currentTime / duration) * 100 || 0}%`,
              height: '100%',
              backgroundColor: '#f00',
              borderRadius: 2,
            }}/>
          </div>
          
          {/* Quality Selector - Always visible */}
          {availableQualities.length > 0 && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setShowQualityMenu(!showQualityMenu); 
                }}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
                title="Video Quality"
                data-testid="quality-selector-button"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                </svg>
                <span style={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}>
                  {getQualityLabel()}
                </span>
              </button>
              
              {/* Quality Menu Dropdown */}
              {showQualityMenu && (
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    right: 0,
                    marginBottom: 8,
                    backgroundColor: 'rgba(28,28,28,0.95)',
                    borderRadius: 8,
                    overflow: 'hidden',
                    minWidth: 140,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                    backdropFilter: 'blur(12px)',
                  }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="quality-menu"
                >
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: 12, color: '#888', fontWeight: 600 }}>
                    Quality
                  </div>
                  {availableQualities.map((q) => (
                    <button
                      key={q.id}
                      onClick={(e) => { e.stopPropagation(); handleQualityChange(q.id); }}
                      data-testid={`quality-option-${q.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '10px 12px',
                        background: currentQuality === q.id ? 'rgba(139,92,246,0.3)' : 'transparent',
                        border: 'none',
                        color: '#fff',
                        fontSize: 14,
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {q.label}
                        {q.height >= 2160 && <span style={{ fontSize: 10, backgroundColor: '#8b5cf6', padding: '1px 4px', borderRadius: 3, fontWeight: 700 }}>UHD</span>}
                        {q.height >= 1080 && q.height < 2160 && <span style={{ fontSize: 10, backgroundColor: '#3b82f6', padding: '1px 4px', borderRadius: 3, fontWeight: 700 }}>HD</span>}
                      </span>
                      {currentQuality === q.id && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#8b5cf6">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* AirPlay Button - Always show */}
          <button
            onClick={(e) => { e.stopPropagation(); handleAirPlay(); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            title="AirPlay (Apple TV)"
            data-testid="airplay-button"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M6 22h12l-6-6-6 6zM21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v-2H3V5h18v12h-4v2h4c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
            </svg>
          </button>
          
          {/* Chromecast Button - Always show */}
          <button
            onClick={(e) => { e.stopPropagation(); handleChromecast(); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            title="Cast to TV (Chromecast)"
            data-testid="chromecast-button"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11zm20-7H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
            </svg>
          </button>
          
          {/* Fullscreen */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              
              // Check if already in fullscreen
              const isFullscreen = document.fullscreenElement || 
                                   document.webkitFullscreenElement ||
                                   document.mozFullScreenElement ||
                                   document.msFullscreenElement;
              
              if (isFullscreen) {
                // Exit fullscreen
                if (document.exitFullscreen) {
                  document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                  document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                  document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                  document.msExitFullscreen();
                }
              } else {
                // Enter fullscreen - ALWAYS prefer container (shows ads/overlays) over video element
                const videoEl = videoRef.current;
                const container = containerRef.current;
                
                isVideoElementFullscreen.current = false;
                
                // Try container fullscreen first (works on all modern browsers + iOS 15.4+)
                if (container?.requestFullscreen) {
                  container.requestFullscreen().catch(() => {
                    // Fallback to video element for old iOS
                    if (videoEl?.webkitEnterFullscreen) {
                      isVideoElementFullscreen.current = true;
                      videoEl.webkitEnterFullscreen();
                    }
                  });
                } else if (container?.webkitRequestFullscreen) {
                  try {
                    container.webkitRequestFullscreen();
                  } catch {
                    // Fallback to video element for old iOS
                    if (videoEl?.webkitEnterFullscreen) {
                      isVideoElementFullscreen.current = true;
                      videoEl.webkitEnterFullscreen();
                    }
                  }
                } else if (videoEl?.webkitEnterFullscreen) {
                  // Last resort: iOS video-element fullscreen (ads won't overlay)
                  isVideoElementFullscreen.current = true;
                  videoEl.webkitEnterFullscreen();
                } else if (container?.mozRequestFullScreen) {
                  container.mozRequestFullScreen();
                } else if (container?.msRequestFullscreen) {
                  container.msRequestFullscreen();
                }
              }
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            title="Fullscreen"
            data-testid="fullscreen-button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
            </svg>
          </button>
        </div>
      )}
      
      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});

VODPlayer.displayName = 'VODPlayer';

export default VODPlayer;
